import * as React from 'react';
import {
    Stack,
    Divider,
    Typography,
    Box,
    ToggleButton,
    CircularProgress,
} from '@mui/material';
import {
    TextInput,
    required,
    ReferenceArrayInput,
    useChoicesContext,
    useInput,
} from 'react-admin';

export const RoleInputs = () => {
    return (
        <Stack flex={1}>
            <RoleBasicInputs />
            <Divider />
            <RolePermissionInputs />
        </Stack>
    );
};

const RoleBasicInputs = () => {
    return (
        <Stack>
            <Typography variant="h6">Basic Info</Typography>
            <TextInput
                source="name"
                validate={required()}
                helperText={false}
            />
        </Stack>
    );
};

const RolePermissionInputs = () => {
    return (
        <Stack>
            <Typography variant="h6">Permissions</Typography>
            <ReferenceArrayInput label="" reference="permissions" source="permission_ids"
                sort={{ field: 'name', order: 'ASC' }}
                perPage={100}>
                <PermissionToggleInput />
            </ReferenceArrayInput>
        </Stack>
    );
};

type PermissionChoice = { id: number; name: string };

const PermissionToggleInput = () => {
    const { allChoices, isLoading } = useChoicesContext();
    const { field } = useInput({ source: 'permission_ids' });
    const selected: number[] = field.value ?? [];

    const groups = React.useMemo(() => {
        const byModule = new Map<string, PermissionChoice[]>();
        for (const choice of (allChoices ?? []) as PermissionChoice[]) {
            const module = choice.name.includes(':')
                ? choice.name.slice(0, choice.name.indexOf(':'))
                : 'other';
            const list = byModule.get(module) ?? [];
            list.push(choice);
            byModule.set(module, list);
        }
        return [...byModule.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([module, perms]) => ({
                module,
                perms: perms.sort((a, b) => a.name.localeCompare(b.name)),
            }));
    }, [allChoices]);

    const toggle = (id: number) => {
        field.onChange(
            selected.includes(id)
                ? selected.filter((x) => x !== id)
                : [...selected, id]
        );
    };

    if (isLoading) {
        return <CircularProgress size={24} sx={{ mt: 1 }} />;
    }

    return (
        <Stack spacing={2} mt={1}>
            {groups.map(({ module, perms }) => (
                <Box key={module}>
                    <Typography
                        variant="subtitle2"
                        sx={{ textTransform: 'capitalize', color: 'text.secondary', mb: 0.5 }}
                    >
                        {module.replace(/_/g, ' ')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {perms.map((perm) => {
                            const action = perm.name.includes(':')
                                ? perm.name.slice(perm.name.indexOf(':') + 1)
                                : perm.name;
                            return (
                                <ToggleButton
                                    key={perm.id}
                                    value={perm.id}
                                    size="small"
                                    selected={selected.includes(perm.id)}
                                    onClick={() => toggle(perm.id)}
                                    title={perm.name}
                                    color="primary"
                                    sx={{ textTransform: 'none', borderRadius: '10px' }}
                                >
                                    {action}
                                </ToggleButton>
                            );
                        })}
                    </Box>
                </Box>
            ))}
        </Stack>
    );
};